import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, Order } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { cancelToasts } from "../printing/print-notices";

interface Props {
  order: Order;
  /** The owner's code, given just before. */
  authorization: Authorization;
  onDone: (order: Order) => void;
  onClose: () => void;
}

/**
 * Beheer › Bestellingen: the owner sets a mistaken order right (Furkan, 01/10/2026). First the warning
 * (only mistakes, at most 5 a day, the shop is responsible), then the order is cancelled with the
 * reason. It never disappears: it stays in the list as cancelled, with who did it.
 */
export function CorrectionDialog({ order, authorization, onDone, onClose }: Props) {
  const { api, toast } = useTill();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.request<Order>("POST", `/orders/${order.id}/cancel`, {
        staffToken: authorization.token,
        // setting a mistake right: the customer gets no mail, as when an order is deleted
        body: { reason, correction: true },
      });
      onDone(updated);
      toast(t.orders.cancelledBy(order.code, authorization.staff.name));
      // an open order: the kitchen gets GEANNULEERD (no cash back: it was a mistake)
      for (const text of cancelToasts(updated.prints)) toast(text);
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t.orders.correctTitle(order.code)}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose} data-autofocus>
            {t.orders.back}
          </button>
          <button type="button" className="btn danger" disabled={busy || reason === ""} onClick={() => void cancel()}>
            {t.orders.confirmCancel}
          </button>
        </>
      }
    >
      <div className="delbox">
        <p>
          {t.orders.correctOnly[0]}
          <b>{t.orders.correctOnly[1]}</b>
          {t.orders.correctOnly[2]}
        </p>
        <p>
          {t.orders.correctLimit[0]}
          <b>{t.orders.correctLimit[1]}</b>
          {t.orders.correctLimit[2]}
        </p>
        <p className="note">{t.orders.correctResponsibility}</p>
      </div>
      <label className="field">
        {t.orders.cancelReason}
        <select
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        >
          <option value="">{t.orders.chooseReason}</option>
          {t.orders.correctReasons.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </label>
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
